import React, { useState } from "react";
import { Link } from "react-router-dom";

/* PACKAGES */
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import { PopupModal } from "react-calendly";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* CONFIG */
import { animation, containerAnimation } from "config";

/* ICONS */
import { MdOutlineArrowOutward } from "react-icons/md";
import { BsCalendar2DateFill } from "react-icons/bs";

/* FORM VALIDATIONS */
import * as yup from "yup";
import { useFormik } from "formik";

/* API */
import { newsletterAPI } from "service/api";

/* VALIDATION SCHEMA */
const waitlistValidation = yup.object().shape({
  email: yup
    .string()
    .email()
    .min(1, "Email Address is required")
    .required("Email Address is required."),
});

/* TAGS */
const tags = [
  {
    id: 1,
    name: "Task Management",
    link: "/blogs/task-management",
  },
  {
    id: 2,
    name: "Employee Engagement",
    link: "/blogs/employee-engagement",
  },
  {
    id: 3,
    name: "Performance Management",
    link: "/blogs/performance-management",
  },
  {
    id: 4,
    name: "Change Management",
    link: "/blogs/change-management",
  },
  {
    id: 5,
    name: "OKR Management",
    link: "/blogs/okr-management",
  },
  {
    id: 6,
    name: "Project Planning",
    link: "/blogs/project-planning",
  },
  {
    id: 7,
    name: "Project Management",
    link: "/blogs/project-management",
  },
  {
    id: 8,
    name: "Team collaboration",
    link: "/blogs/team-collaboration",
  },
];

const ManagingWorkplace = () => {
  const [calendlyModal, setCalendlyModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  /* FORM HANDLING */
  const formHandling = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: waitlistValidation,
    onSubmit: (values) => {
      const obj = {
        email: values.email,
      };
      setFormLoading(true);
      newsletterAPI(obj)
        .then((res) => {
          if (res?.status) {
            toast.success(
              "Congratulations! 🎉 You have subscribed to our newsletter.",
            );
          } else {
            toast.error(
              "We were not able to capture your details. Please try again.",
            );
          }
          setFormLoading(false);
        })
        .catch((e) => {
          setFormLoading(false);
          toast.error(e?.response?.data?.message);
        })
        .finally((res) => {
          formHandling.resetForm();
          setFormLoading(false);
        });
    },
  });

  /* SMALL TAGS BUTTON */
  const SmallTagsButton = (props) => {
    return (
      <Link to={props.link}>
        <Button className="shadow-none hover:shadow-none bg-greyBg hover:bg-logoOrange rounded-md text-sm font-asap font-normal text-greyText hover:text-white normal-case px-4 py-2 transition-all duration-300 ease-in-out">
          {props.label}
        </Button>
      </Link>
    );
  };

  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-transparent"
    >
      {/* TOAST CONTAINER */}
      <ToastContainer theme="colored" closeOnClick autoClose={5000} />

      {/* CONTENT */}
      <motion.div variants={animation} className="py-5 xl:py-12 lg:py-12">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3">
            {/* CONTENT */}
            <div className="col-span-2 border-r-0 xl:border-r lg:border-r border-greyBorder">
              <div className="pr-0 xl:pr-8 lg:pr-8">
                <h1 className="mt-5 text-xl xl:text-3xl lg:text-3xl font-asap font-bold text-black leading-snug">
                  The Manager’s Force Multiplier: Why Automating Culture is the
                  Way to Scale
                </h1>

                <div className="mt-5">
                  <p className="text-lg font-asap font-normal text-greyText leading-relaxed">
                    In the race for market share, businesses often overlook
                    their most strained asset: the middle manager. Today’s
                    managers are expected to be coaches, therapists, and
                    high-level strategists all at once. The result?{" "}
                    <span className="font-asap font-bold text-black">
                      Manager burnout is at an all-time high
                    </span>
                    , and when managers redline, the culture they oversee begins
                    to crumble.
                  </p>
                  <p className="mt-3 text-lg font-asap font-normal text-greyText leading-relaxed">
                    For a business to scale, culture cannot be a manual task
                    that lives on a manager's "to-do" list. It must be an
                    automated, systemic advantage. Here is how manual
                    culture-building is hurting your business and how{" "}
                    <span className="font-asap font-bold text-black">
                      Aligned Rewards
                    </span>{" "}
                    provides the solution.
                  </p>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      The Bottleneck: "Culture by Manual Effort"
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Most businesses rely on managers to "remember" to be
                      appreciative. But when deadlines loom and the "364 days of
                      work" pile up, the manual effort of recognition is the
                      first thing to be dropped.
                    </p>
                    <ul className="ml-8 mt-3 list-disc space-y-2">
                      <li className="text-lg font-asap font-normal text-greyText leading-relaxed">
                        <span className="font-asap font-bold text-black">
                          The Business Risk:
                        </span>{" "}
                        When recognition is dependent on a manager’s mood or
                        memory, it becomes inconsistent. Inconsistency breeds
                        perceived favoritism, which destroys team trust and
                        lowers retention.
                      </li>
                      <li className="text-lg font-asap font-normal text-greyText leading-relaxed">
                        <span className="font-asap font-bold text-black">
                          The Hidden Cost:
                        </span>{" "}
                        High-value managers spend hours every month on
                        administrative "culture tasks" tracking anniversaries,
                        managing spreadsheets for bonuses, or chasing down
                        performance data. This is an expensive use of leadership
                        time.
                      </li>
                    </ul>
                  </div>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      Aligned Rewards: The Force Multiplier for Leaders
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      <span className="font-asap font-bold text-black">
                        Aligned Rewards
                      </span>{" "}
                      transforms culture from a manual chore into an automated
                      engine. We give managers their time back while ensuring no
                      employee feels "unseen."
                    </p>
                    <div className="mt-3 ml-5">
                      <h5 className="text-lg font-asap font-bold text-black">
                        A. Automated Milestones & "Nudge" Technology
                      </h5>
                      <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                        Instead of a manager having to remember work
                        anniversaries or specific project deadlines, the Aligned
                        Rewards platform automates the celebration of
                        milestones. Our "nudge" technology prompts managers at
                        the exact right moment to offer feedback based on
                        real-time task completion.
                      </p>
                      <ul className="ml-8 mt-3 list-disc space-y-2">
                        <li className="text-lg font-asap font-normal text-greyText leading-relaxed">
                          <span className="font-asap font-bold text-black">
                            The Business Impact:
                          </span>{" "}
                          You ensure 100% "recognition coverage" without adding
                          a single minute of administrative burden to your
                          leadership team.
                        </li>
                      </ul>
                    </div>
                    <div className="mt-5 ml-5">
                      <h5 className="text-lg font-asap font-bold text-black">
                        B. Decentralizing the Load (Peer-to-Peer Recognition)
                      </h5>
                      <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                        The greatest burden on a manager is being the "sole
                        source" of validation. Aligned Rewards implements a
                        <span className="font-asap font-bold text-black">
                          Peer-to-Peer recognition ecosystem
                        </span>
                        . This allows the team to sustain its own morale.
                      </p>
                      <ul className="ml-8 mt-3 list-disc space-y-2">
                        <li className="text-lg font-asap font-normal text-greyText leading-relaxed">
                          <span className="font-asap font-bold text-black">
                            The Business Impact:
                          </span>{" "}
                          By empowering employees to reward one another, the
                          "visibility" happens organically. This reduces the
                          manager's emotional labor and creates a self-healing
                          culture that thrives even when the boss is in
                          back-to-back meetings.
                        </li>
                      </ul>
                    </div>
                    <div className="mt-5 ml-5">
                      <h5 className="text-lg font-asap font-bold text-black">
                        C. Bridging the "Project-to-Payout" Gap
                      </h5>
                      <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                        In most companies, there is a massive time gap between a
                        win and the reward. Aligned Rewards bridges this with
                        Instant Micro-Rewards. When a task is marked complete in
                        our integrated task management system, recognition can
                        be triggered immediately.
                      </p>
                      <ul className="ml-8 mt-3 list-disc space-y-2">
                        <li className="text-lg font-asap font-normal text-greyText leading-relaxed">
                          <span className="font-asap font-bold text-black">
                            The Business Impact
                          </span>{" "}
                          Instant gratification reinforces positive behavior far
                          more effectively than a bonus paid out six months
                          later. You get more "behavioral ROI" for every dollar
                          spent on rewards.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      Turning Qualitative Culture into Quantitative Data
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      The biggest frustration for executives is that "culture"
                      often feels like a "soft" metric. Aligned Rewards changes
                      that. Our platform provides a{" "}
                      <span className="font-asap font-bold text-black">
                        Command Center for Human Capital.
                      </span>
                    </p>
                    <ul className="ml-8 mt-3 list-disc space-y-2">
                      <li className="text-lg font-asap font-normal text-greyText leading-relaxed">
                        <span className="font-asap font-bold text-black">
                          Spot the "Quiet Resigners":
                        </span>{" "}
                        Leadership can see which teams are falling silent before
                        the resignations start.
                      </li>
                      <li className="text-lg font-asap font-normal text-greyText leading-relaxed">
                        <span className="font-asap font-bold text-black">
                          Identify Future Leaders:
                        </span>{" "}
                        Our data identifies the "culture carriers"—those who are
                        consistently recognized by their peers for
                        collaboration, not just those who shout the loudest in
                        meetings.
                      </li>
                    </ul>
                  </div>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      The Strategic Conclusion
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      You cannot scale a business if your culture requires
                      manual maintenance. If your managers are the only ones
                      responsible for making employees feel "seen," your
                      organization will eventually hit a ceiling.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      <span className="font-asap font-bold text-black">
                        Aligned Rewards
                      </span>{" "}
                      is more than a rewards platform; it is a{" "}
                      <span className="font-asap font-bold text-black">
                        management operating system
                      </span>
                      . By automating the "soft" side of business, we allow your
                      leadership team to focus on "hard" growth, ensuring that
                      as you scale, your culture doesn't just survive—it
                      accelerates.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      <span className="font-asap font-bold text-black">
                        Is your culture manual or automatic?
                      </span>{" "}
                      See{" "}
                      <span className="italic">
                        how Aligned Rewards can take the burden off your
                        managers and put your culture on autopilot.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* SIDEBAR */}
            <div className="col-span-1 mt-5 xl:mt-0 lg:mt-0">
              <div className="pl-0 xl:pl-8 lg:pl-8  h-fit sticky top-24">
                {/* NEWSLETTER */}
                <motion.div
                  variants={animation}
                  className="pb-8 border-b border-greyBorder"
                >
                  <div
                    variants={animation}
                    className="bg-greyBg bg-opacity-50 border border-greyBorder rounded-lg p-5 xl:p-8 lg:p-8"
                  >
                    <h5 className="text-2xl font-asap font-bold">
                      Join Our Community
                    </h5>
                    <p className="text-lg font-asap font-normal text-greyText mt-3">
                      Join us so that we can send you updates on new features,
                      valuable tips, expert strategies and our exclusive offers
                      delivered directly to your inbox.
                    </p>
                    <form className="mt-5" onSubmit={formHandling.handleSubmit}>
                      <input
                        type="email"
                        placeholder="Type you email address"
                        name="newsletterEmail"
                        id="newsletterEmail"
                        value={formHandling.values.email}
                        onChange={formHandling.handleChange}
                        className={`w-full block bg-white px-4 py-3 rounded-lg text-sm font-asap font-bold text-black border ${
                          formHandling.errors.email &&
                          formHandling.touched.email
                            ? "border-red-500"
                            : "border-greyBorder"
                        } border-greyBorder focus:outline-brandColor placeholder:font-asap font-normal`}
                      />
                      <Button
                        disabled={formLoading}
                        type={formLoading ? "button" : "submit"}
                        className="group mt-3 w-full flex items-center gap-1 justify-center shadow-none hover:shadow-none font-asap font-bold text-white rounded-md bg-brandColor normal-case text-sm hover:bg-opacity-80 transition-all duration-300 ease-in-out"
                      >
                        {formLoading ? "Please wait.." : "Subscribe"}
                        <MdOutlineArrowOutward className="w-4 h-4 text-white" />
                      </Button>
                    </form>
                  </div>
                </motion.div>

                {/* Schedule a Demo */}
                <motion.div variants={animation} className="pt-8">
                  <BsCalendar2DateFill className="w-10 h-10 text-black" />
                  <h5 className="mt-3 text-2xl font-asap font-bold leading-snug">
                    Learn how Aligned Rewards can be useful for your
                    organization
                  </h5>
                  <p className="mt-3 text-lg font-asap font-normal text-greyText">
                    Experience a guided walkthrough of all our features in this
                    exclusive video!
                  </p>
                  <Button
                    className="mt-5 shadow-none hover:shadow-none bg-brandColor text-white font-asap font-bold text-sm normal-case"
                    onClick={() => setCalendlyModal(!calendlyModal)}
                  >
                    Schedule a Demo
                  </Button>
                </motion.div>

                {/* TAGS */}
                <motion.div variants={animation} className="pt-8 hidden">
                  <h5 className="text-xl xl:text-2xl lg:text-2xl font-asap font-bold text-black tracking-tight">
                    Tags
                  </h5>
                  <div className="flex items-center flex-wrap gap-2 mt-5">
                    {tags.map((item) => (
                      <React.Fragment key={item.id}>
                        <SmallTagsButton label={item.name} link={item.link} />
                      </React.Fragment>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* CALENDLY MODAL */}
      <PopupModal
        open={calendlyModal}
        onModalClose={() => setCalendlyModal(!calendlyModal)}
        rootElement={document.getElementById("root")}
        url="https://calendly.com/contact-ucp8/30min"
      />
    </motion.section>
  );
};

export default ManagingWorkplace;
